<template>
  <v-menu :close-on-content-click="false" location="bottom start" @click.stop>
    <template #activator="{ props: menuProps }">
      <button
        v-bind="menuProps"
        type="button"
        class="tag-picker__add"
        :class="`tag-picker__add--${size}`"
        :aria-label="size === 'sm' ? 'Add tag' : undefined"
        @click.stop>
        <v-icon :size="size === 'md' ? 14 : 16">mdi-plus</v-icon>
        <span v-if="size === 'md'">Add tag</span>
      </button>
    </template>
    <v-card min-width="240" class="tag-picker__menu">
      <v-card-text class="pa-2">
        <div
          v-for="def in tagDefs"
          :key="def.key"
          class="tag-picker__item"
          :class="{ 'tag-picker__item--disabled': def.auto }"
          @click.stop="!def.auto && onToggle(def.key, !currentTags.includes(def.key))">
          <span class="tag-picker__dot" :style="{ background: TAG_COLORS[def.color] || TAG_COLORS.grey }" />
          <span class="tag-picker__label">{{ def.label }}</span>
          <span v-if="def.auto" class="tag-picker__auto">auto</span>
          <v-icon v-if="currentTags.includes(def.key)" size="16" color="accent" class="tag-picker__check">
            mdi-check
          </v-icon>
        </div>
      </v-card-text>
    </v-card>
  </v-menu>
</template>

<script setup>
import { computed } from "vue";
import { TAG_COLORS } from "@/constants/tagColors";
import { filterUserTags } from "@/utils/tags";

const props = defineProps({
  tags: { type: Array, default: () => [] },
  tagDefs: { type: Array, default: () => [] },
  size: { type: String, default: "sm", validator: (v) => ["sm", "md"].includes(v) },
});

const emit = defineEmits(["update:tags"]);

const currentTags = computed(() => props.tags || []);

function onToggle(key, checked) {
  const userTags = filterUserTags(currentTags.value, props.tagDefs);
  emit("update:tags", checked ? [...userTags, key] : userTags.filter((t) => t !== key));
}
</script>

<style lang="scss" scoped>
.tag-picker__add {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px dashed rgba($lh_color_navy, 0.25);
  background: transparent;
  color: $lh_color_blue_gray;
  font-family: $lh_font_family_dm_sans;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: color $lh_transition_fast, border-color $lh_transition_fast, background $lh_transition_fast;

  &:hover {
    color: $lh_color_blue;
    border-color: rgba($lh_color_blue, 0.45);
    background: rgba($lh_color_blue, 0.04);
  }

  &--sm {
    width: 26px;
    height: 26px;
    padding: 0;
    flex: 0 0 auto;
    border-radius: 50%;
  }

  &--md {
    height: 26px;
    padding: 0 12px 0 9px;
    border-radius: 13px;
  }
}

.tag-picker__menu {
  max-height: 320px;
  overflow-y: auto;
  border-radius: 12px !important;
}

.tag-picker__item {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 7px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-family: $lh_font_family_dm_sans;
  transition: background $lh_transition_fast;

  &:hover {
    background: $lh_color_snowy_white;
  }

  &--disabled {
    cursor: default;
    opacity: 0.6;

    &:hover {
      background: transparent;
    }
  }
}

.tag-picker__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: 0 0 auto;
}

.tag-picker__label {
  font-size: 13.5px;
  font-weight: 500;
  color: $lh_color_navy;
  flex: 1 1 auto;
}

.tag-picker__auto {
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: $lh_color_blue_gray;
}

.tag-picker__check {
  flex: 0 0 auto;
}
</style>
